import React, { useEffect } from 'react';
import routes from '../../../../../Routes/routes';
import { getUrlParameters, TAB_INDEX } from "../../External/Model/Model";
import { REQUEST_ACCESS_STEP_MODEL } from "../Model/Model";

/**
 * IAmNewHereMainApp
 *
 * Component to handle the “I Am New Here” flow.
 * Manages step navigation, button enable/disable logic, and triggers request creation.
 * Validates visitor consent, email, and phone number before enabling navigation.
 *
 * @author Karen Yunuen
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * @param {Function} navigate - Function to navigate programmatically (react-router).
 * @param {Object} steps - Object representing the steps of the QR request flow.
 * @param {Function} setSteps - Function to update the steps state.
 * @param {number} activeStep - Current active step index.
 * @param {Array} visitorList - List of selected visitors.
 * @param {Object} requestAccessItem - Request access object with details and validations.
 * @param {Function} setBtnDisabled - Function to enable/disable the Next button.
 * @param {Function} handleCreate - Function to create the request access.
 * @param {Function} navigateStep - Function to navigate to a specific step.
 * @param {Object} userData - Current user/visitor data object.
 * @param {boolean} isValidEmail - Flag indicating whether the email is valid.
 * @param {boolean} isValidPhoneNumber - Flag indicating whether the phone number is valid.
 * @param {Function} resetData - Function to reset all form and state data.
 *
 * @returns {Object} An object containing handler functions:
 * - handleClickNext: Function to navigate to the next step or trigger create request.
 * - handleNavigateBack: Function to navigate to the previous step or reset data.
 */
const IAmNewHereMainApp = ({
                               navigate,
                               steps,
                               setSteps,
                               activeStep,
                               visitorList,
                               requestAccessItem,
                               setBtnDisabled,
                               handleCreate,
                               navigateStep,
                               userData,
                               isValidEmail,
                               isValidPhoneNumber,
                               resetData
                           }) => {

    /**
     * Set initial steps when component mounts.
     *
     * @dependencies [] - Runs only once on mount.
     */
    useEffect(() => {
        setSteps({ ...REQUEST_ACCESS_STEP_MODEL.I_AM_NEW_HERE });
    }, []);

    /**
     * Enable or disable the Next button based on current step and form validation.
     *
     * @dependencies [steps, activeStep, visitorList, requestAccessItem?.requestReason, requestAccessItem?.privacyConsent, userData]
     */
    useEffect(() => {
        switch (activeStep) {
            case TAB_INDEX.ONE:
                if (!requestAccessItem?.privacyConsent || !userData.isValid() || !isValidEmail || !isValidPhoneNumber)
                    setBtnDisabled(true);
                else
                    setBtnDisabled(false);
                break;
            case TAB_INDEX.TWO:
                if (!requestAccessItem?.isValid())
                    setBtnDisabled(true);
                else
                    setBtnDisabled(false);
                break;
            default:
                break;
        }
    }, [steps, activeStep, visitorList, requestAccessItem?.requestReason, requestAccessItem?.privacyConsent, userData]);

    /**
     * Handle the Next button click and navigate to the appropriate step.
     */
    const handleClickNext = () => {
        const completeSteps = {
            [TAB_INDEX.ONE]: () => navigateStep(TAB_INDEX.TWO),
            [TAB_INDEX.TWO]: () => navigateStep(TAB_INDEX.THREE),
            [TAB_INDEX.THREE]: () => handleCreate(TAB_INDEX.FOUR),
            [TAB_INDEX.FOUR]: () => navigate(getUrlParameters({ url: routes.qrRequestAccess.main }))
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    /**
     * Handle the Back button click and navigate to the previous step or reset data.
     */
    const handleNavigateBack = () => {
        const completeSteps = {
            [TAB_INDEX.ONE]: () => {
                resetData();
                navigate(getUrlParameters({ url: routes.qrRequestAccess.main }));
            },
            [TAB_INDEX.TWO]: () => navigateStep(TAB_INDEX.ONE),
            [TAB_INDEX.THREE]: () => navigateStep(TAB_INDEX.TWO),
            [TAB_INDEX.FOUR]: () => navigateStep(TAB_INDEX.THREE)
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    return {
        handleClickNext,
        handleNavigateBack
    };
};

export default IAmNewHereMainApp;
